import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { User } from 'lucide-react';
import { startTransition, useCallback, useMemo, useState } from 'react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger
} from '@/components/ui/multi-select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import NoAssigneeIcon from '@/pages/investments/direct/[id]/due-diligence/NoAssigneeIcon';
import { getInitials } from '@/utils/getInitials';

const getDisplayName = (name: null | string | undefined, email: string) =>
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional: empty string should also fall back
  name || email;

const ALL_EMPLOYEES = gql`
  query AllEmployees {
    hrEmployees {
      edges {
        node {
          id
          name
          workEmail
          email
          userId {
            id
            email
          }
          departmentId {
            name
          }
        }
      }
    }
  }
`;

interface Employee {
  departmentId?: {
    name: string;
  } | null;
  email?: null | string;
  id: string;
  name?: null | string;
  userId?: {
    email: string;
    id: string;
  } | null;
  workEmail?: null | string;
}

// Helper to get the best email for an employee
const getEmployeeEmail = (employee: Employee): string =>
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- intentional: empty string should also fall back
  employee.workEmail || employee.email || employee.userId?.email || '';

interface TeamMemberSelectorProps {
  disabled?: boolean;
  initialSelectedIds?: string[];
  multiple?: boolean;
  onChange?: (ids: string[]) => void;
}

export function TeamMemberSelector({
  initialSelectedIds = [],
  multiple = false,
  onChange
}: TeamMemberSelectorProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);

  const [searchTerm, setSearchTerm] = useState('');

  const { data } = useQuery<{
    hrEmployees?: {
      edges?: {
        node: Employee;
      }[];
    };
  }>(ALL_EMPLOYEES, {
    fetchPolicy: 'cache-first'
  });

  const allEmployees: Employee[] = useMemo(() => {
    if (!data?.hrEmployees?.edges) return [];
    return data.hrEmployees.edges.map((edge: { node: Employee }) => edge.node);
  }, [data]);

  const employees: Employee[] = useMemo(() => {
    if (!searchTerm) return allEmployees;
    const searchLower = searchTerm.toLowerCase();
    return allEmployees.filter(employee => {
      const name = employee.name?.toLowerCase() ?? '';
      const email = getEmployeeEmail(employee).toLowerCase();
      return name.includes(searchLower) || email.includes(searchLower);
    });
  }, [allEmployees, searchTerm]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const selectedValues = useMemo(() => {
    return [...initialSelectedIds, ...selectedIds];
  }, [initialSelectedIds, selectedIds]);

  const handleValuesChange = useCallback(
    (values: string[]) => {
      if (values.includes('__none__')) {
        setSelectedIds([]);
        startTransition(() => {
          onChange?.([]);
        });
        return;
      }

      const filteredValues = values.filter(v => v !== '__none__');

      let newSelectedIds: string[];
      if (!multiple) {
        if (filteredValues.length === 0) {
          newSelectedIds = [];
        } else {
          newSelectedIds = [filteredValues[filteredValues.length - 1]];
        }
      } else {
        newSelectedIds = filteredValues;
      }

      setSelectedIds(newSelectedIds);
      startTransition(() => {
        onChange?.(newSelectedIds);
      });
    },
    [multiple, onChange]
  );

  const selectedEmployees = useMemo(() => {
    if (selectedValues.length === 0) return [];
    const selectedSet = new Set([...initialSelectedIds, ...selectedValues]);
    return allEmployees.filter(employee => selectedSet.has(employee.id));
  }, [allEmployees, selectedValues, initialSelectedIds]);

  const hasSelection = selectedValues.length > 0;

  if (!hasSelection) {
    return (
      <MultiSelect
        onValuesChange={handleValuesChange}
        registerItems={false}
        values={selectedValues}
      >
        <MultiSelectTrigger
          className="hover:bg-muted !flex !h-8 !min-h-0 !w-8 !items-center !justify-center !gap-0 !rounded-full !border-0 !bg-transparent !p-0 shadow-none [&>svg:first-child]:!h-6 [&>svg:first-child]:!w-6 [&>svg:last-child]:!hidden"
          variant="ghost"
        >
          <NoAssigneeIcon size={20} />
        </MultiSelectTrigger>
        <MultiSelectContent
          onSearchChange={handleSearchChange}
          search={{ placeholder: 'Search employees...' }}
        >
          <MultiSelectGroup>
            <MultiSelectItem
              badgeLabel={
                <div className="flex items-center gap-2">
                  <div className="bg-muted flex h-6 w-6 items-center justify-center rounded-full">
                    <User className="text-muted-foreground h-3 w-3" />
                  </div>
                  <span>No assignee</span>
                </div>
              }
              value="__none__"
            >
              <div className="flex items-center gap-2">
                <div className="bg-muted flex h-6 w-6 items-center justify-center rounded-full">
                  <User className="text-muted-foreground h-3 w-3" />
                </div>
                <span>No assignee</span>
              </div>
            </MultiSelectItem>
            {employees.length === 0 ? (
              <div className="text-muted-foreground py-4 text-center text-sm">
                No employees found.
              </div>
            ) : (
              <>
                <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                  Team members
                </div>
                {employees.map(employee => (
                  <MultiSelectItem
                    badgeLabel={
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs">
                            {getInitials(
                              getDisplayName(
                                employee.name,
                                getEmployeeEmail(employee)
                              )
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <span>
                          {getDisplayName(
                            employee.name,
                            getEmployeeEmail(employee)
                          )}
                        </span>
                      </div>
                    }
                    key={employee.id}
                    value={employee.id}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {getInitials(
                            getDisplayName(
                              employee.name,
                              getEmployeeEmail(employee)
                            )
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <span>
                        {getDisplayName(
                          employee.name,
                          getEmployeeEmail(employee)
                        )}
                      </span>
                    </div>
                  </MultiSelectItem>
                ))}
              </>
            )}
          </MultiSelectGroup>
        </MultiSelectContent>
      </MultiSelect>
    );
  }

  if (multiple) {
    return (
      <MultiSelect
        onValuesChange={handleValuesChange}
        registerItems={false}
        values={selectedValues}
      >
        <MultiSelectTrigger className="h-auto min-h-8 w-auto border-0 !p-0 shadow-none hover:bg-white">
          <div className="flex -space-x-2">
            {selectedEmployees.length > 4 ? (
              <>
                {selectedEmployees.slice(0, 4).map(employee => (
                  <Avatar
                    className="ring-background ring-2"
                    key={employee.id}
                  >
                    <AvatarFallback className="text-xs">
                      {getInitials(
                        getDisplayName(
                          employee.name,
                          getEmployeeEmail(employee)
                        )
                      )}
                    </AvatarFallback>
                  </Avatar>
                ))}
                <div className="ring-background bg-muted flex h-8 w-8 items-center justify-center rounded-full text-xs ring-2">
                  +{selectedEmployees.length - 4}
                </div>
              </>
            ) : (
              selectedEmployees.map(employee => (
                <Avatar
                  className="ring-background ring-2"
                  key={employee.id}
                >
                  <AvatarFallback className="text-xs">
                    {getInitials(
                      getDisplayName(employee.name, getEmployeeEmail(employee))
                    )}
                  </AvatarFallback>
                </Avatar>
              ))
            )}
          </div>
        </MultiSelectTrigger>
        <MultiSelectContent
          onSearchChange={handleSearchChange}
          search={{ placeholder: 'Search employees...' }}
        >
          <MultiSelectGroup>
            <MultiSelectItem
              badgeLabel={
                <div className="flex items-center gap-2">
                  <div className="bg-muted flex h-6 w-6 items-center justify-center rounded-full">
                    <User className="text-muted-foreground h-3 w-3" />
                  </div>
                  <span>No assignee</span>
                </div>
              }
              value="__none__"
            >
              <div className="flex items-center gap-2">
                <div className="bg-muted flex h-6 w-6 items-center justify-center rounded-full">
                  <User className="text-muted-foreground h-3 w-3" />
                </div>
                <span>No assignee</span>
              </div>
            </MultiSelectItem>
            {employees.length === 0 ? (
              <div className="text-muted-foreground py-4 text-center text-sm">
                No employees found.
              </div>
            ) : (
              <>
                <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                  Team members
                </div>
                {employees.map(employee => (
                  <MultiSelectItem
                    badgeLabel={
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs">
                            {getInitials(
                              getDisplayName(
                                employee.name,
                                getEmployeeEmail(employee)
                              )
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <span>
                          {getDisplayName(
                            employee.name,
                            getEmployeeEmail(employee)
                          )}
                        </span>
                      </div>
                    }
                    key={employee.id}
                    value={employee.id}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {getInitials(
                            getDisplayName(
                              employee.name,
                              getEmployeeEmail(employee)
                            )
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <span>
                        {getDisplayName(
                          employee.name,
                          getEmployeeEmail(employee)
                        )}
                      </span>
                    </div>
                  </MultiSelectItem>
                ))}
              </>
            )}
          </MultiSelectGroup>
        </MultiSelectContent>
      </MultiSelect>
    );
  }

  const firstSelectedEmployee =
    selectedEmployees.length > 0 ? selectedEmployees[0] : undefined;

  return (
    <MultiSelect
      onValuesChange={handleValuesChange}
      registerItems={false}
      values={selectedValues}
    >
      <MultiSelectTrigger className="h-auto min-h-8 w-auto border-0 !p-0 shadow-none hover:bg-white">
        {firstSelectedEmployee != null ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Avatar>
                <AvatarFallback>
                  {getInitials(
                    getDisplayName(
                      firstSelectedEmployee.name,
                      getEmployeeEmail(firstSelectedEmployee)
                    )
                  )}
                </AvatarFallback>
              </Avatar>
            </TooltipTrigger>
            <TooltipContent>
              {getDisplayName(
                firstSelectedEmployee.name,
                getEmployeeEmail(firstSelectedEmployee)
              )}
            </TooltipContent>
          </Tooltip>
        ) : (
          <div className="bg-muted flex h-8 w-8 items-center justify-center rounded-full">
            <User className="text-muted-foreground h-4 w-4" />
          </div>
        )}
      </MultiSelectTrigger>
      <MultiSelectContent
        onSearchChange={handleSearchChange}
        search={{ placeholder: 'Search employees...' }}
      >
        <MultiSelectGroup>
          <MultiSelectItem
            badgeLabel={
              <div className="flex items-center gap-2">
                <div className="bg-muted flex h-6 w-6 items-center justify-center rounded-full">
                  <User className="text-muted-foreground h-3 w-3" />
                </div>
                <span>No assignee</span>
              </div>
            }
            value="__none__"
          >
            <div className="flex items-center gap-2">
              <div className="bg-muted flex h-6 w-6 items-center justify-center rounded-full">
                <User className="text-muted-foreground h-3 w-3" />
              </div>
              <span>No assignee</span>
            </div>
          </MultiSelectItem>
          {employees.length === 0 ? (
            <div className="text-muted-foreground py-4 text-center text-sm">
              No employees found.
            </div>
          ) : (
            <>
              <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                Team members
              </div>
              {employees.map(employee => (
                <MultiSelectItem
                  badgeLabel={
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {getInitials(
                            getDisplayName(
                              employee.name,
                              getEmployeeEmail(employee)
                            )
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <span>
                        {getDisplayName(
                          employee.name,
                          getEmployeeEmail(employee)
                        )}
                      </span>
                    </div>
                  }
                  key={employee.id}
                  value={employee.id}
                >
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs">
                        {getInitials(
                          getDisplayName(
                            employee.name,
                            getEmployeeEmail(employee)
                          )
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <span>
                      {getDisplayName(
                        employee.name,
                        getEmployeeEmail(employee)
                      )}
                    </span>
                  </div>
                </MultiSelectItem>
              ))}
            </>
          )}
        </MultiSelectGroup>
      </MultiSelectContent>
    </MultiSelect>
  );
}
